import { compact } from 'es-toolkit';
import { buildSummaryDetailsCopy } from '@/components/shared/link-preview/summary/details';
import { formatBytes } from '@/lib/formatters/bytes';
import {
  getSummaryLengthLabel,
  MODEL_LABEL,
  resolveModelOptionFromCanonical,
} from '@/lib/link-preview/summary-options';
import type { TranscriptSource } from '@/lib/link-preview/types';
import { formatDurationMs } from '@/lib/utils/time';
import { toTrimmedNonEmptyString } from '@/lib/utils/type-guards';
import type { SummaryStreamMetadata } from './summary-metadata';

type SummaryDiagnosticsOptions = {
  metadata: SummaryStreamMetadata;
  modeLabel?: string | null;
  canonicalModelFallback?: string | null;
  includeAdvancedTip?: string | null;
  powerUserEnabled?: boolean;
  includeLatency?: boolean;
  includeFinishReason?: boolean;
};

// Normalizes the metadata emitted by summarize streams into tooltip-friendly lines.
export const buildSummaryDiagnosticsLines = ({
  metadata,
  modeLabel,
  canonicalModelFallback,
  includeAdvancedTip,
  powerUserEnabled,
  includeLatency = true,
  includeFinishReason = true,
}: SummaryDiagnosticsOptions): string[] => {
  const lines: string[] = [];

  if (modeLabel) {
    lines.push(`Mode · ${modeLabel}`);
  }

  const availableImages = metadata.availableImages ?? null;
  const includedImages = metadata.includedImages ?? null;
  const shouldIncludeImages = metadata.shouldIncludeImages;

  const hasAvailableImages = typeof availableImages === 'number' && availableImages > 0;
  if (hasAvailableImages) {
    if (shouldIncludeImages === false || includedImages === 0) {
      lines.push('Images excluded from prompt.');
    } else {
      const resolvedIncluded = includedImages ?? (shouldIncludeImages ? availableImages : 0) ?? 0;
      const boundedIncluded = Math.min(resolvedIncluded, availableImages);
      lines.push(`Images included · ${boundedIncluded} of ${availableImages}`);
    }
  }

  const canonicalModelId = metadata.resolvedModel ?? metadata.requestedModel ?? canonicalModelFallback ?? null;
  const canonicalOption = canonicalModelId ? resolveModelOptionFromCanonical(canonicalModelId) : null;
  const resolvedModelLabel =
    canonicalOption && MODEL_LABEL[canonicalOption] ? MODEL_LABEL[canonicalOption] : (canonicalModelId ?? null);
  const summaryLengthLabel = getSummaryLengthLabel(metadata.summaryLength ?? null);

  const detailCopy = buildSummaryDetailsCopy({
    resolvedModelLabel,
    resolvedSummaryLengthLabel: summaryLengthLabel,
    modelId: canonicalModelId,
    summaryTruncated: metadata.truncated,
    contentTruncated: metadata.contentTruncated,
    sourceCharacters: metadata.sourceCharacters,
    contentBudgetCharacters: metadata.contentBudgetCharacters,
    transcriptAvailable: metadata.transcriptAvailable,
    transcriptSource: normalizeTranscriptSource(metadata.transcriptSource),
    usage: metadata.usage,
    cost: metadata.cost,
    powerUserEnabled: powerUserEnabled ?? Boolean(metadata.cost || metadata.usage),
  });
  lines.push(...compact(detailCopy.split('\n')));

  if (includeLatency && typeof metadata.responseTimeMs === 'number' && metadata.responseTimeMs > 0) {
    lines.push(`Latency · ${formatLatency(metadata.responseTimeMs)}`);
  }

  if (includeFinishReason && metadata.finishReason) {
    lines.push(`Finish reason · ${metadata.finishReason}`);
  }

  // Surface the basic video attributes so support teams can validate run characteristics at a glance.
  if (typeof metadata.videoDurationSeconds === 'number' && metadata.videoDurationSeconds > 0) {
    lines.push(`Video duration · ${formatDurationMs(metadata.videoDurationSeconds * 1000, { fallback: '—' })}`);
  }

  if (typeof metadata.videoSizeBytes === 'number' && metadata.videoSizeBytes > 0) {
    lines.push(`Video size · ${formatBytes(metadata.videoSizeBytes, { fallback: '—' })}`);
  }

  if (metadata.videoContentType) {
    lines.push(`Video type · ${metadata.videoContentType}`);
  }

  if (metadata.videoSource) {
    lines.push(`Video source · ${metadata.videoSource}`);
  }

  const normalizedTip = toTrimmedNonEmptyString(includeAdvancedTip);
  if (normalizedTip) {
    lines.push(normalizedTip);
  }

  return lines;
};

export const formatSummaryDiagnostics = (options: SummaryDiagnosticsOptions): string =>
  buildSummaryDiagnosticsLines(options).join('\n');

const formatLatency = (valueMs: number): string => {
  if (!Number.isFinite(valueMs) || valueMs <= 0) {
    return '—';
  }
  if (valueMs < 1000) {
    return `${Math.round(valueMs)} ms`;
  }
  const seconds = valueMs / 1000;
  const formatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: seconds >= 10 ? 0 : 1,
    minimumFractionDigits: seconds >= 10 ? 0 : 1,
  });
  return `${formatter.format(seconds)} s`;
};

const isTranscriptSource = (value: string): value is TranscriptSource => {
  switch (value) {
    case 'youtubei': {
      return true;
    }
    case 'captionTracks': {
      return true;
    }
    case 'embedded': {
      return true;
    }
    case 'yt-dlp': {
      return true;
    }
    case 'youtube-media': {
      return true;
    }
    case 'apify': {
      return true;
    }
    case 'html': {
      return true;
    }
    case 'whisper': {
      return true;
    }
    case 'podcastTranscript': {
      return true;
    }
    case 'unavailable': {
      return true;
    }
    case 'unknown': {
      return true;
    }
    default: {
      return false;
    }
  }
};

const normalizeTranscriptSource = (value: unknown): TranscriptSource | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value !== 'string') {
    return 'unknown';
  }
  return isTranscriptSource(value) ? value : 'unknown';
};
